import { toError } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { LinearProgress, Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { invalidatePlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/api/useGetPlacementCandidate";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { useCreateBanDispute } from "../../api/useCreateBanDispute";
import { BanDisputeStep } from "../../hooks/useDisputeBanSteps";
import { DISPUTE_REASON } from "../../types/createBanDispute.schema";
import { DisputeConfirmationStep } from "./Steps/DisputeConfirmationStep";
import { DisputeDetailsStep } from "./Steps/DisputeDetailsStep";
import { DisputeReasonStep } from "./Steps/DisputeReasonStep";

const banDisputeFormSchema = z.object({
  reason: z.enum([
    DISPUTE_REASON.ATTENDED_INTERVIEW,
    DISPUTE_REASON.EMERGENCY,
    DISPUTE_REASON.OTHER,
  ]),
  description: z.string().min(20, "Please provide at least 20 characters"),
  evidenceFile: z.instanceof(File).optional(),
});

export type BanDisputeFormData = z.infer<typeof banDisputeFormSchema>;

interface BanDisputeFormProps {
  banId: string;
  workerId: string;
  currentStep: BanDisputeStep;
  setCurrentStep: (step: BanDisputeStep) => void;
}

export function BanDisputeForm(props: BanDisputeFormProps) {
  const { banId, workerId, currentStep, setCurrentStep } = props;
  const queryClient = useQueryClient();

  const { showErrorToast } = useToast();
  const progress = (currentStep / 3) * 100;

  const { control, handleSubmit, watch } = useForm<BanDisputeFormData>({
    resolver: zodResolver(banDisputeFormSchema),
    defaultValues: {
      reason: DISPUTE_REASON.ATTENDED_INTERVIEW,
      description: "",
      evidenceFile: undefined,
    },
    mode: "onChange",
  });
  const selectedReason = watch("reason");

  const { mutateAsync: createBanDispute } = useCreateBanDispute();

  const submitForm = useCallback(
    async (data: BanDisputeFormData) => {
      try {
        await createBanDispute({ banId, ...data, evidenceFile: data.evidenceFile });
        await invalidatePlacementCandidate(queryClient, workerId);
        logEvent(APP_V2_APP_EVENTS.DISPUTE_BAN_SUBMITTED, { banId, data });
        setCurrentStep(BanDisputeStep.CONFIRMATION);
      } catch (error) {
        const errorMessage = toError(error);
        logEvent(APP_V2_APP_EVENTS.DISPUTE_BAN_SUBMITTED_FAILURE, {
          banId,
          data,
          error: errorMessage,
        });
        showErrorToast("Failed to submit form. Please try again.");
      }
    },
    [banId, createBanDispute, showErrorToast, setCurrentStep, workerId, queryClient]
  );

  return (
    <Stack spacing={8}>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 5,
          backgroundColor: (theme) => theme.palette.background.secondary,
        }}
      />
      <form onSubmit={handleSubmit(submitForm)}>
        {currentStep === BanDisputeStep.REASON && (
          <Controller
            control={control}
            name="reason"
            render={({ field }) => (
              <DisputeReasonStep
                selectedReason={field.value}
                onReasonSelected={field.onChange}
                onNextClicked={() => {
                  setCurrentStep(BanDisputeStep.DETAILS);
                }}
              />
            )}
          />
        )}

        {currentStep === BanDisputeStep.DETAILS && selectedReason && (
          <DisputeDetailsStep reason={selectedReason} control={control} />
        )}

        {currentStep === BanDisputeStep.CONFIRMATION && <DisputeConfirmationStep />}
      </form>
    </Stack>
  );
}
